import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { Link } from 'react-router-dom';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import {
  advancedOperators,
  advancedDatePickerOperator,
  advancedNumericOperators,
  advancedBooleanOperators,
  advancedEnumOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { formatPercent, formatPrice, getFormattedDate, chipStyles } from '../../../utils/helpers';
import { PaymentFrequency, LegacyArchiveReason } from '../../../utils/constants';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { getCommissionsInfoByType, UserInfo } from '../../../store/GeneralStore';

export const DefaultProvisionalAssetGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.PROVISIONAL_ASSET, id: params.row.id, data: params.row })
        }}
      />
    ),
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'id',
    headerName: 'ID',
    sortable: false,
    filterable: false,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'assetNumber',
    headerName: 'Number',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/provisional-asset/${params.row.id}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    pinned: 'left',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'accountName',
    headerName: 'Customer',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/customer/${params.row.accountId}`}>
        <div className="flex items-center text-small">{(params.value || params.row.accountId) as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'salesAgents'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'salesAgentName',
    headerName: 'Sales Agent',
    renderCell: (params: GridCellParams) => params.value || params.row.salesAgent,
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'suppliers'),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'supplierName',
    headerName: 'Supplier',
    renderCell: (params: GridCellParams) => params.value || params.row.supplier,
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'tsdName',
    headerName: 'TSD',
    renderCell: (params: GridCellParams) => params.value || params.row.tsd,
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'ultimateParent',
    headerName: 'Ultimate Parent',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/customer/${params.row.ultimateParentId}`}>
        <div className="flex items-center text-small">{`${(params?.value as string) ?? ''}`}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsExperienceAnalyst',
    headerName: 'CEA',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('user', 'cea').map((us) => (us as UserInfo).email),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsExperienceAnalystEmail',
    headerName: 'CEA Email',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'location',
    headerName: 'Provisional Location',
    sortable: false,
    filterable: false,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'circuitId',
    headerName: 'Circuit ID',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'tsdTrackingNumber',
    headerName: 'TSD Tracking Number',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'productName',
    headerName: 'Product',
    renderCell: (params: GridCellParams) => params.value || params.row.product2Id,
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(PaymentFrequency ?? {}),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'paymentFrequency',
    headerName: 'Payment Frequency',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    hideByDefault: true,
    renderCell: (params: GridCellParams) => {
      const value = params.value as string;
      if (!value) return '';
      return (
        <Chip
          label={value}
          size="small"
          sx={chipStyles(value)}
          title={value}
        />
      );
    },
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: Object.values(PaymentFrequency ?? {}),
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'paymentFrequencyOverride',
    headerName: 'Payment Frequency Override',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    renderCell: (params: GridCellParams) => {
      const value = params.value as string;
      if (!value) return '';
      return (
        <Chip
          label={value}
          size="small"
          sx={chipStyles(value)}
          title={value}
        />
      );
    },
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'active',
    headerName: 'Provisional Active',
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'fromId',
    headerName: 'Provisional From ID',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'origin',
    headerName: 'Provisional Origin',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'toId',
    headerName: 'Provisional To ID',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualDecommissionedDate',
    headerName: 'Actual Decommission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualFirstCommissionRun',
    headerName: 'Actual First Commission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'actualFirstSpiffRun',
    headerName: 'Actual First SPIFF Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastCommissionRun',
    headerName: 'Last Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    width: 150,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'installDate',
    headerName: 'Install Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedDecommissionDate',
    headerName: 'Expected Decommission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'disconnectDate',
    headerName: 'Disconnect Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'cancelledDate',
    headerName: 'Cancelled Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsValidatedDate',
    headerName: 'Commissions Validated Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'createdAt',
    headerName: 'Created Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return value ? new Date(value) : '';
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsValidationStatus',
    headerName: 'Commissions Validation Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionsValidationSubStatus',
    headerName: 'Commissions Validation Sub Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'legacyArchiveReason',
    headerName: 'Legacy Archive Reason',
    valueOptions: Object.values(LegacyArchiveReason ?? {}),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionableMrc',
    headerName: 'Commissionable MRC',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedGrossMrr',
    headerName: 'Gross MRR',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedNetMrr',
    headerName: 'Net MRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'mrc',
    headerName: 'Total MRC',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'commissionableNrc',
    headerName: 'Commissionable NRC',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedGrossNrr',
    headerName: 'Gross NRR',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'expectedNetNrr',
    headerName: 'Net NRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'nonCommissionableNrc',
    headerName: 'Non Commissionable NRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'nonRecurringCommission',
    headerName: 'Non Recurring Commission %',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'recurringCommission',
    headerName: 'Recurring Commission %',
    sortable: true,
    filterable: true,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'provisionalType',
    headerName: 'Provisional Type',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'rpmAccountCount',
    headerName: 'RPM Account Count',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    field: 'openCommissionsTicket',
    headerName: 'Open Ticket',
    sortable: true,
    filterable: true,
    width: 130,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedBooleanOperators()
  }
];
